/**
 * Mixpanel wrapper for Vue (adriel_frontend).
 *
 * Uses the global `window.mixpanel` object loaded by the CDN snippet
 * in public/index.html (Node 10 cannot install mixpanel-browser via npm).
 *
 * Adapted from adriel-fe-react/trackers/mixpanel/mixpanel.ts
 */

const MIXPANEL_TOKEN = process.env.VUE_APP_MIXPANEL_TOKEN;
const MIXPANEL_ENABLED =
    process.env.VUE_APP_ENABLE_MIXPANEL !== 'FALSE' &&
    process.env.VUE_APP_ENABLE_MIXPANEL !== 'false';

/**
 * Email patterns to exclude from Mixpanel tracking.
 * Users whose email matches any of these patterns will be completely
 * ignored — no events, no identification, no super properties.
 */
const INTERNAL_EMAIL_PATTERNS = [/@adriel\./i];

let initialized = false;
let identifiedEmail;

const isInternalUser = () =>
    identifiedEmail != null &&
    INTERNAL_EMAIL_PATTERNS.some(function (pattern) {
        return pattern.test(identifiedEmail);
    });

const shouldTrack = () =>
    typeof window !== 'undefined' &&
    Boolean(MIXPANEL_TOKEN) &&
    MIXPANEL_ENABLED &&
    !isInternalUser();

const getMixpanel = () => window.mixpanel;

export const initMixpanel = () => {
    if (!shouldTrack() || initialized) return;

    const mp = getMixpanel();
    if (!mp) {
        console.warn('[Mixpanel] SDK not loaded from CDN');
        return;
    }

    mp.init(MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV !== 'production',
        track_pageview: false,
        ignore_dnt: false,
    });
    initialized = true;
};

export const identifyUser = ({ userId, email }) => {
    // Store email first so shouldTrack() can evaluate internal-user check
    if (email) {
        identifiedEmail = email;
    }

    if (!shouldTrack()) return;
    initMixpanel();

    const mp = getMixpanel();
    if (!mp) return;

    if (userId) {
        mp.identify(String(userId));
    }

    if (email) {
        mp.people.set({
            $email: email,
        });
    }
};

export const registerSuperProperties = (properties) => {
    if (!shouldTrack()) return;
    initMixpanel();

    const mp = getMixpanel();
    if (!mp) return;

    mp.register(properties);
};

export const mixpanelLogger = {
    logEvent(eventName, payload) {
        if (!shouldTrack()) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('[Mixpanel debug] ' + eventName, {
                    ...(payload || {}),
                });
            }
            return;
        }

        initMixpanel();

        var mp = getMixpanel();
        if (!mp) return;

        try {
            mp.track(eventName, payload || {});
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Mixpanel logger error:', error);
        }
    },
};

export const resetMixpanel = () => {
    identifiedEmail = undefined;
    if (!initialized) return;

    var mp = getMixpanel();
    if (!mp) return;

    try {
        mp.reset();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Mixpanel reset error:', error);
    }
    initialized = false;
};
